import React, { useState, useEffect } from 'react';
import FourPillars from '../components/FourPillars';

const AbandonedFreightPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  const slideBgStyle = {
    backgroundImage: 'linear-gradient(120deg, rgba(15,23,42,0.9), rgba(249,115,22,0.8)), url(/logistics.webp)',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  };

  const StepIcon = ({ step }) => {
    const iconSize = 140;
    const common = {
      width: iconSize,
      height: iconSize,
      viewBox: '0 0 140 140',
      fill: 'none',
    };
    
    switch (step) {
      case 1:
        // Legal/Document icon with frame
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="40" y="40" width="60" height="60" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M50 55h40M50 70h40M50 85h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="90" cy="85" r="6" fill="currentColor"/>
          </svg>
        );
      case 2:
        // Magnifying glass/search icon with square frame and L-brackets
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="70" cy="70" r="24" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M88 88l18 18" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
            <circle cx="70" cy="70" r="8" fill="currentColor" opacity="0.3"/>
          </svg>
        );
      case 3:
        // Distribution/network icon with frame
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="45" cy="45" r="14" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="95" cy="45" r="14" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="45" cy="95" r="14" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="95" cy="95" r="14" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M59 45h36M45 59v36M95 59v36M59 95h36" stroke="currentColor" strokeWidth="3"/>
            <circle cx="70" cy="70" r="10" fill="currentColor"/>
          </svg>
        );
      case 4:
        // Document/checkmark icon with frame
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="35" width="70" height="70" rx="6" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 55h50M45 70h50M45 85h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M80 85l8 8 16-16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const steps = [
    {
      step: 1,
      title: 'Legal Due Diligence & Owner Notification',
      description: 'We verify abandonment status, dispatch statutory notices, and document all communications to protect you from future liability.',
    },
    {
      step: 2,
      title: 'Detailed Freight Assessment & Valuation',
      description: 'Contents are inspected, photographed, and matched with real-time market data to establish true recovery potential.',
    },
    {
      step: 3,
      title: 'Strategic Disposition & Channel Distribution',
      description: 'Goods are routed to the highest-value channel—wholesale, export, or recycling—while honoring brand or geographic restrictions.',
    },
    {
      step: 4,
      title: 'Compliance Documentation & Final Settlement',
      description: 'You receive a complete audit pack: chain-of-custody, notice logs, sales results, and environmental metrics for any recycled materials.',
    },
  ];

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'turn-into-opportunity',
        'hidden-cost',
        'legal-compliance',
        'key-benefits',
        'tailored-solutions',
        'equipment-items'
      ];
      
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <main className="services">
      <div className="container">
        <div className="sv-content-wrapper">
          <div className="sv-main-content">
        {/* Hero */}
        <section className="sv-hero">
          <div className="sv-eyebrow">Abandoned Freight Resolution</div>
          <h1 className="sv-title">Abandoned Freight Resolution</h1>
          <p className="sv-subtitle">Convert costly dock congestion into recovered capital with our compliant, end-to-end abandoned freight management service.</p>
        </section>

        {/* 4-Step Process Carousel */}
        <section className="ti-steps-carousel">
          <div className="ti-carousel-slide-container">
            <div 
              className="ti-carousel-slides" 
              style={{ transform: `translateX(-${currentStep * 100}%)` }}
            >
              {steps.map((step, index) => (
                <div key={step.step} className="ti-carousel-slide" style={slideBgStyle}>
                  <div className="ti-carousel-icon">
                    <StepIcon step={step.step} />
                  </div>
                  <div className="ti-carousel-content">
                    <div className="ti-step-badge">STEP {step.step}</div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="ti-carousel-indicators">
            {steps.map((_, index) => (
              <button
                key={index}
                className={`ti-carousel-indicator ${index === currentStep ? 'active' : ''}`}
                onClick={() => goToStep(index)}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </section>

      {/* Turn Abandoned Freight Into Opportunity */}
      <section id="turn-into-opportunity" className="sv-overview">
        <h2 className="sv-section-title">Turn Abandoned Freight Into Opportunity</h2>
        <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
          Every pallet of unclaimed freight on your dock represents tied-up capital, wasted space, and potential liability exposure. Our comprehensive abandoned freight resolution service transforms this challenge into an opportunity to recover value and reclaim critical operational space.
        </p>
      </section>

      {/* The Hidden Cost of Unclaimed Inventory */}
      <section id="hidden-cost" className="sv-overview">
        <h2 className="sv-section-title">The Hidden Cost of Unclaimed Inventory</h2>
        <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
          Abandoned freight silently drains your resources through multiple channels:
        </p>
        <ul className="sv-bullets sv-list" style={{ marginBottom: '24px' }}>
          <li>Each occupied pallet position costs $1,200-$1,800 annually in direct storage expenses</li>
          <li>Blocked staging areas reduce throughput capacity during peak periods</li>
          <li>Staff working around congestion experience reduced productivity and increased error rates</li>
          <li>Managing exceptions diverts valuable resources away from core business operations</li>
          <li>Premium space allocated to zero-value inventory creates significant opportunity cost</li>
        </ul>
        <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155' }}>
          Without a structured resolution process, these costs continue accumulating while recovery potential steadily diminishes. Most operations discover that indirect costs outweigh direct storage expenses by 3-4x, making abandoned freight one of the most expensive forms of supply chain waste.
        </p>
      </section>

      {/* Navigating the Legal Compliance Maze */}
      <section id="legal-compliance" className="sv-overview">
        <h2 className="sv-section-title">Navigating the Legal Compliance Maze</h2>
        <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
          The regulatory framework varies significantly by:
        </p>
        <ol style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginLeft: '24px', marginBottom: '24px' }}>
          <li style={{ marginBottom: '12px' }}>Transportation mode (ocean, air, ground)</li>
          <li style={{ marginBottom: '12px' }}>Jurisdiction (state, federal, international)</li>
          <li style={{ marginBottom: '12px' }}>Goods classification (general, hazardous, regulated)</li>
          <li style={{ marginBottom: '12px' }}>Original shipping terms and documentation</li>
        </ol>
        <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155' }}>
          Our meticulous process ensures full compliance with all applicable statutes, including proper notice periods, documentation requirements, and lien provisions. This creates a liability shield that protects your organization from future claims while providing a clear path to lawful disposition.
        </p>
      </section>

      {/* Key Benefits */}
      <section id="key-benefits" className="sv-features">
        <h2 className="sv-section-title">Key Benefits</h2>
        <div className="sv-features-grid">
          <div className="sv-feature">
            <h4>Immediate Space Recovery</h4>
            <p>Free up valuable dock and warehouse space within days, boosting operational capacity and efficiency.</p>
          </div>
          <div className="sv-feature">
            <h4>Comprehensive Liability Protection</h4>
            <p>Our meticulous documentation and statutory compliance process creates a bulletproof shield against future claims.</p>
          </div>
          <div className="sv-feature">
            <h4>Capital Recovery</h4>
            <p>Convert stranded inventory into immediate cash flow through our optimized disposition channels.</p>
          </div>
          <div className="sv-feature">
            <h4>Complete Turnkey Solution</h4>
            <p>One call handles everything—from legal assessment to final disposition—letting your team focus on core operations.</p>
          </div>
        </div>
      </section>

      {/* Tailored Solutions for Every Freight Situation */}
      <section id="tailored-solutions" className="sv-overview">
        <h2 className="sv-section-title">Tailored Solutions for Every Freight Situation</h2>
        <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '32px' }}>
          Our systematic approach begins with proper classification to determine the optimal resolution pathway:
        </p>
        
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Refused Delivery</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
            When consignee explicitly declines acceptance at time of delivery attempt. Requires specific documentation of refusal and triggers accelerated disposition timeline with reduced notice requirements in most jurisdictions.
          </p>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Unclaimed Shipments</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
            When delivery attempts fail and standard holding period expires without response. Requires documented delivery attempts and formal notice period before legal abandonment can be established.
          </p>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Damaged Goods</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
            When condition issues trigger rejection or insurance claims during delivery process. May involve third-party inspection, insurance coordination, and specialized disposition channels for partial-value recovery.
          </p>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Unidentified Freight</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
            When documentation is insufficient to determine rightful ownership or destination. Requires forensic tracing through carrier records and extended public notice periods before disposition.
          </p>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Disputed Shipments</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
            When payment or contractual issues prevent normal delivery completion. May require mediation services, payment processing, and specialized documentation of resolution attempts.
          </p>
        </div>
      </section>

      {/* Equipment and Items We Accept */}
      <section id="equipment-items" className="sv-overview">
        <h2 className="sv-section-title">Equipment and Items We Accept</h2>
        <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
          Our comprehensive service handles a wide range of technology equipment and electronics, ensuring proper handling of data-sensitive items with secure chain-of-custody documentation.
        </p>
        <div style={{ overflowX: 'auto', marginBottom: '32px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#0f172a' }}>Category</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#0f172a' }}>Included Items</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Computing Equipment</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Desktops, laptops, tablets, servers, workstations</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Networking Equipment</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Routers, switches, firewalls, access points, cabling</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Data Center Equipment</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Rack servers, storage arrays, UPS systems, PDUs</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Mobile Devices</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Smartphones, cell phones, enterprise mobile computers</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Storage Media</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Hard drives, SSDs, tape drives, USB drives</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Peripherals</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Monitors, keyboards, mice, scanners, webcams</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Printing Equipment</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Printers, copiers, multi‑function devices</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Communication Systems</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>IP phones, conference systems, PBX equipment</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Point of Sale Equipment</td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Terminals, barcode scanners, receipt printers</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 16px', color: '#334155', fontWeight: '600' }}>Security Hardware</td>
                <td style={{ padding: '12px 16px', color: '#334155' }}>Security cameras, access‑control systems, card readers</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Four Pillars */}
      <FourPillars />

      {/* CTA */}
      <section className="sv-cta">
        <div className="sv-cta-inner">
          <h3>Ready to resolve your abandoned freight challenges?</h3>
          <p>Get a free assessment and discover how we can clear your docks and recover value.</p>
          <div className="sv-cta-actions">
            <a className="btn btn-primary" href="tel:+18778739336">Call (877) 873-9336</a>
            <a className="btn btn-secondary" href="mailto:info@globoflo.com">Email Us</a>
          </div>
        </div>
      </section>
          </div>

          {/* Quick Links Sidebar */}
          <aside className="sv-quick-links">
            <h3 className="sv-quick-links-title">Abandoned Freight Resolution</h3>
            <nav className="sv-quick-links-nav">
              <ul>
                <li>
                  <button 
                    className={activeSection === 'turn-into-opportunity' ? 'active' : ''}
                    onClick={() => scrollToSection('turn-into-opportunity')}
                  >
                    Turn Abandoned Freight Into Opportunity
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'hidden-cost' ? 'active' : ''}
                    onClick={() => scrollToSection('hidden-cost')}
                  >
                    The Hidden Cost of Unclaimed Inventory
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'legal-compliance' ? 'active' : ''}
                    onClick={() => scrollToSection('legal-compliance')}
                  >
                    Navigating the Legal Compliance Maze
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'key-benefits' ? 'active' : ''}
                    onClick={() => scrollToSection('key-benefits')}
                  >
                    Key Benefits
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'tailored-solutions' ? 'active' : ''}
                    onClick={() => scrollToSection('tailored-solutions')}
                  >
                    Tailored Solutions for Every Freight Situation
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'equipment-items' ? 'active' : ''}
                    onClick={() => scrollToSection('equipment-items')}
                  >
                    Equipment and Items We Accept
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default AbandonedFreightPage;
